import {
  DisconnectReason,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  TrackEvent,
  type Participant,
  type RemoteTrack,
  type TrackPublication,
} from 'livekit-client';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Bugsnag from '@bugsnag/js';
import { useWebinarT } from '@/hooks/useWebinarT';
import { LiveKitRoom as LiveKitSessionRoot } from '@livekit/components-react';
import LiveKitRoomContent from './admin/LiveKitRoomContent';
import type { TriggerEventInput } from '@/features/live/types';
import type { VideoLayoutState, VideoPlayerStateControl } from './admin/VideoLiveView';
import { Loader2, Volume2 } from 'lucide-react';
import { AudioRenderer } from './AudioRendered';
import type { RootState } from '@/app/store';
import { useAppSelector } from '@/app/hooks';
import { buildScreenShareOptions } from '@/lib/screenShareCapture';
import {
  AUDIO_RESUME_DELAY_MS,
  createAudioPlaybackResumer,
  getRemoteAudioElements,
  isRemoteAudioBlocked,
} from '@/lib/audioPlaybackResume';
import { useScreenWakeLock } from './useScreenWakeLock';
import {
  buildCameraCaptureOptions,
  buildCameraPublishOptions,
  capCameraEncodings,
} from '@/lib/cameraCapture';
import {
  ROOM_AUTO_RETRY_DELAY_MS,
  classifyRoomError,
  decideAfterDisconnect,
  decideOnForegroundReturn,
  hasRoomConnectionTimedOut,
  shouldAutoRetry,
  type DisconnectDecision,
  type RoomConnectionState,
  type RoomFailure,
} from '@/lib/liveRoomConnection';

const isBugsnagEnabled = Boolean(import.meta.env.VITE_BUGSNAG_API_KEY);

/** Message fixe : Bugsnag regroupe tous les échecs de connexion en une issue, le détail est en métadonnées. */
const ROOM_FAILURE_REPORT_MESSAGE = 'Live room connection failed';

const reportRoomFailure = (failure: RoomFailure, role: string): void => {
  if (!isBugsnagEnabled) return;
  Bugsnag.notify(new Error(ROOM_FAILURE_REPORT_MESSAGE), (event) => {
    event.severity = 'warning';
    event.addMetadata('roomConnection', { role, kind: failure.kind, reason: failure.message });
  });
};

/** Un épisode arrière-plan / retour par notification, avec ce que la page a décidé au réveil. */
const reportForegroundReturn = (
  role: string,
  details: { state: RoomConnectionState; action: string; hiddenForMs: number | null },
): void => {
  if (!isBugsnagEnabled) return;
  Bugsnag.notify(new Error('Live room foreground return'), (event) => {
    event.severity = 'info';
    event.addMetadata('roomConnection', { role, ...details });
  });
};

function toRoomConnectionState(state: Room['state']): RoomConnectionState {
  if (state === 'connected') return 'connected';
  if (state === 'reconnecting' || state === 'signalReconnecting') return 'reconnecting';
  if (state === 'connecting') return 'connecting';
  return 'disconnected';
}

/** True si ce participant publie déjà au moins une piste locale « scène » (cam, micro ou partage). */
function localParticipantHasStagePublication(lp: Room['localParticipant']): boolean {
  const sources = [Track.Source.Camera, Track.Source.Microphone, Track.Source.ScreenShare] as const;
  return sources.some((src) => lp.getTrackPublication(src)?.track != null);
}

/**
 * Règle présentateurs multiples : sans opt-in explicite (`presenterTracksEnabled`), un nouvel arrivant
 * ne doit pas « voler » la scène — mais un présentateur déjà en ligne ne doit pas être coupé quand
 * un collègue se connecte (bug où tous perdaient le flux au ParticipantConnected).
 */
function computeAdminPresenterPublishAllowed(
  room: Room,
  removedFromLive: boolean | undefined,
  presenterTracksEnabled: boolean | undefined,
): boolean {
  if (removedFromLive) {
    return false;
  }
  const hasOtherPresenters = Array.from(room.remoteParticipants.values()).some((p) =>
    p.identity?.startsWith('presenter-'),
  );
  if (!hasOtherPresenters) {
    return true;
  }
  if (presenterTracksEnabled === true) {
    return true;
  }
  return localParticipantHasStagePublication(room.localParticipant);
}

function isPresenterPublishingMedia(
  room: Room,
  isAdmin: boolean | undefined,
  removedFromLive: boolean | undefined,
  presenterTracksEnabled: boolean | undefined,
): boolean {
  if (isAdmin !== true) {
    return false;
  }
  return computeAdminPresenterPublishAllowed(room, removedFromLive, presenterTracksEnabled);
}

function isVideoCaptureBroken(lp: Room['localParticipant'], wantCamera: boolean): boolean {
  if (!wantCamera) {
    return false;
  }
  const pub = lp.getTrackPublication(Track.Source.Camera);
  const track = pub?.track;
  return (
    !track ||
    !(track instanceof LocalVideoTrack) ||
    track.isMuted ||
    track.mediaStreamTrack.readyState === 'ended'
  );
}

function isAudioCaptureBroken(lp: Room['localParticipant'], wantMic: boolean): boolean {
  if (!wantMic) {
    return false;
  }
  const pub = lp.getTrackPublication(Track.Source.Microphone);
  const track = pub?.track;
  return (
    !track ||
    !(track instanceof LocalAudioTrack) ||
    track.isMuted ||
    track.mediaStreamTrack.readyState === 'ended'
  );
}

async function recoverVideoAfterDeviceChange(
  room: Room,
  lp: Room['localParticipant'],
  videoDeviceId: string | undefined,
  cameraEnabled: boolean | undefined,
): Promise<void> {
  const pub = lp.getTrackPublication(Track.Source.Camera);
  const vTrack = pub?.track;
  if (vTrack instanceof LocalVideoTrack) {
    // Toujours les options complètes : livekit-client REMPLACE les contraintes de la piste par
    // celles qu'on lui passe, un { deviceId } seul rouvre donc la caméra sans définition et le pilote
    // sert son format par défaut (640x480). À défaut de cible, on garde le périphérique en cours.
    const targetDeviceId = videoDeviceId ?? vTrack.mediaStreamTrack.getSettings().deviceId;
    await vTrack.restartTrack(buildCameraCaptureOptions(targetDeviceId));
  } else {
    await lp.setCameraEnabled(true);
  }
  if (videoDeviceId && room.switchActiveDevice) {
    await room.switchActiveDevice('videoinput', videoDeviceId);
  }
  await lp.setCameraEnabled(cameraEnabled ?? true);
}

async function recoverAudioAfterDeviceChange(
  room: Room,
  lp: Room['localParticipant'],
  audioDeviceId: string | undefined,
  microphoneEnabled: boolean | undefined,
): Promise<void> {
  const pub = lp.getTrackPublication(Track.Source.Microphone);
  const aTrack = pub?.track;
  if (aTrack instanceof LocalAudioTrack) {
    await aTrack.restartTrack(audioDeviceId ? { deviceId: audioDeviceId } : undefined);
  } else {
    await lp.setMicrophoneEnabled(true);
  }
  if (audioDeviceId && room.switchActiveDevice) {
    await room.switchActiveDevice('audioinput', audioDeviceId);
  }
  await lp.setMicrophoneEnabled(microphoneEnabled ?? true);
}

async function fallbackToggleVideo(
  room: Room,
  lp: Room['localParticipant'],
  videoDeviceId: string | undefined,
  cameraEnabled: boolean | undefined,
): Promise<void> {
  await lp.setCameraEnabled(false);
  await lp.setCameraEnabled(true);
  if (videoDeviceId && room.switchActiveDevice) {
    await room.switchActiveDevice('videoinput', videoDeviceId);
  }
  await lp.setCameraEnabled(cameraEnabled ?? true);
}

async function fallbackToggleAudio(
  room: Room,
  lp: Room['localParticipant'],
  audioDeviceId: string | undefined,
  microphoneEnabled: boolean | undefined,
): Promise<void> {
  await lp.setMicrophoneEnabled(false);
  await lp.setMicrophoneEnabled(true);
  if (audioDeviceId && room.switchActiveDevice) {
    await room.switchActiveDevice('audioinput', audioDeviceId);
  }
  await lp.setMicrophoneEnabled(microphoneEnabled ?? true);
}

async function restoreBrokenTracks(
  room: Room,
  lp: Room['localParticipant'],
  ctx: {
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    videoDeviceId?: string;
    audioDeviceId?: string;
  },
  videoNeedsFix: boolean,
  audioNeedsFix: boolean,
): Promise<void> {
  if (videoNeedsFix) {
    await recoverVideoAfterDeviceChange(room, lp, ctx.videoDeviceId, ctx.cameraEnabled);
  }
  if (audioNeedsFix) {
    await recoverAudioAfterDeviceChange(room, lp, ctx.audioDeviceId, ctx.microphoneEnabled);
  }
}

async function restoreMediaWithFallback(
  room: Room,
  lp: Room['localParticipant'],
  ctx: {
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    videoDeviceId?: string;
    audioDeviceId?: string;
  },
  wantCamera: boolean,
  wantMic: boolean,
  videoNeedsFix: boolean,
  audioNeedsFix: boolean,
): Promise<void> {
  try {
    await restoreBrokenTracks(room, lp, ctx, videoNeedsFix, audioNeedsFix);
  } catch (error) {
    console.warn('LiveKit: reprise média après devicechange', error);
    try {
      if (wantCamera && isVideoCaptureBroken(lp, wantCamera)) {
        await fallbackToggleVideo(room, lp, ctx.videoDeviceId, ctx.cameraEnabled);
      }
      if (wantMic && isAudioCaptureBroken(lp, wantMic)) {
        await fallbackToggleAudio(room, lp, ctx.audioDeviceId, ctx.microphoneEnabled);
      }
    } catch (fallbackError) {
      console.error('LiveKit: échec reprise média (fallback)', fallbackError);
    }
  }
}

async function unpublishLocalParticipantTracks(
  lp: Room['localParticipant'],
  opts: { includeScreenShare: boolean },
): Promise<void> {
  const publications = Array.from(lp.trackPublications.values());
  for (const publication of publications) {
    if (!publication.track) {
      continue;
    }
    if (
      !opts.includeScreenShare &&
      (publication.source === Track.Source.ScreenShare ||
        publication.source === Track.Source.ScreenShareAudio)
    ) {
      continue;
    }
    await lp.unpublishTrack(publication.track);
  }
}

async function unpublishAdminPresenterTracks(
  room: Room,
  removedFromLive: boolean | undefined,
): Promise<void> {
  if (room.state !== 'connected') {
    return;
  }
  try {
    await unpublishLocalParticipantTracks(room.localParticipant, {
      includeScreenShare: removedFromLive === true,
    });
    await room.localParticipant.setCameraEnabled(false);
    await room.localParticipant.setMicrophoneEnabled(false);
  } catch (error) {
    console.error('Error unpublishing presenter tracks:', error);
  }
}

/**
 * Après setCamera / setMicrophone, vérifie que la capture locale n’est pas morte
 * (piste manquante, mediaStreamTrack ended) et applique la même reprise qu’en devicechange.
 */
async function ensureLocalCaptureMatchesIntent(
  room: Room,
  options: {
    wantCam: boolean;
    wantMic: boolean;
    cameraEnabled: boolean | undefined;
    microphoneEnabled: boolean | undefined;
    videoDeviceId: string | undefined;
    audioDeviceId: string | undefined;
  },
): Promise<void> {
  if (room.state !== 'connected') {
    return;
  }
  const lp = room.localParticipant;
  const { wantCam, wantMic, cameraEnabled, microphoneEnabled, videoDeviceId, audioDeviceId } =
    options;

  if (wantCam && isVideoCaptureBroken(lp, true)) {
    try {
      await recoverVideoAfterDeviceChange(room, lp, videoDeviceId, cameraEnabled);
    } catch (e) {
      console.warn('LiveKit: reprise cam après activation', e);
    }
    if (isVideoCaptureBroken(lp, true)) {
      try {
        await fallbackToggleVideo(room, lp, videoDeviceId, cameraEnabled);
      } catch (e) {
        console.error('LiveKit: fallback cam après activation', e);
      }
    }
  }

  if (wantMic && isAudioCaptureBroken(lp, true)) {
    try {
      await recoverAudioAfterDeviceChange(room, lp, audioDeviceId, microphoneEnabled);
    } catch (e) {
      console.warn('LiveKit: reprise micro après activation', e);
    }
    if (isAudioCaptureBroken(lp, true)) {
      try {
        await fallbackToggleAudio(room, lp, audioDeviceId, microphoneEnabled);
      } catch (e) {
        console.error('LiveKit: fallback micro après activation', e);
      }
    }
  }
}

async function recoverLocalMediaAfterDeviceChange(
  room: Room,
  ctx: {
    isAdmin?: boolean;
    isSpeaker?: boolean;
    removedFromLive?: boolean;
    presenterTracksEnabled?: boolean;
    speakerTracksEnabled?: boolean;
    cameraEnabled?: boolean;
    microphoneEnabled?: boolean;
    videoDeviceId?: string;
    audioDeviceId?: string;
  },
): Promise<void> {
  if (room.state !== 'connected') {
    return;
  }

  const publishingPresenter = isPresenterPublishingMedia(
    room,
    ctx.isAdmin,
    ctx.removedFromLive,
    ctx.presenterTracksEnabled,
  );
  const publishingSpeaker = ctx.isSpeaker === true && ctx.speakerTracksEnabled === true;
  const publishing = publishingPresenter || publishingSpeaker;
  if (!publishing) {
    return;
  }

  const wantCamera = publishing && (ctx.cameraEnabled ?? true);
  const wantMic = publishing && (ctx.microphoneEnabled ?? true);
  const lp = room.localParticipant;

  const videoNeedsFix = isVideoCaptureBroken(lp, wantCamera);
  const audioNeedsFix = isAudioCaptureBroken(lp, wantMic);
  if (!videoNeedsFix && !audioNeedsFix) {
    return;
  }

  await restoreMediaWithFallback(room, lp, ctx, wantCamera, wantMic, videoNeedsFix, audioNeedsFix);
}

/** Contrôle du partage d’écran (API LiveKit : le navigateur ouvre ensuite le sélecteur natif). */
export type LiveKitScreenShareControl = {
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => Promise<void>;
  isScreenSharing: () => boolean;
};

const MEDIA_STATE_EVENTS = ['play', 'pause', 'emptied'] as const;

/**
 * Un refus de play() ne déclenche aucun événement média : on l'apprend par la piste
 * (AudioPlaybackFailed). La reprise après clic, elle, passe par l'événement `play` de l'élément.
 */
function useRemoteAudioBlocked(room: Room) {
  const [blocked, setBlocked] = useState(() => isRemoteAudioBlocked(getRemoteAudioElements(room)));

  useEffect(() => {
    const update = () => setBlocked(isRemoteAudioBlocked(getRemoteAudioElements(room)));
    const watchedTracks = new Set<RemoteTrack>();
    const watchTrack = (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio || watchedTracks.has(track)) return;
      watchedTracks.add(track);
      track.on(TrackEvent.AudioPlaybackFailed, update);
      track.on(TrackEvent.AudioPlaybackStarted, update);
    };

    room.remoteParticipants.forEach((participant) =>
      participant.audioTrackPublications.forEach((publication) => {
        if (publication.track) watchTrack(publication.track);
      }),
    );
    room.on(RoomEvent.TrackSubscribed, watchTrack);
    room.on(RoomEvent.TrackUnsubscribed, update);
    room.on(RoomEvent.AudioPlaybackStatusChanged, update);
    MEDIA_STATE_EVENTS.forEach((event) => document.addEventListener(event, update, true));

    return () => {
      watchedTracks.forEach((track) => {
        track.off(TrackEvent.AudioPlaybackFailed, update);
        track.off(TrackEvent.AudioPlaybackStarted, update);
      });
      room.off(RoomEvent.TrackSubscribed, watchTrack);
      room.off(RoomEvent.TrackUnsubscribed, update);
      room.off(RoomEvent.AudioPlaybackStatusChanged, update);
      MEDIA_STATE_EVENTS.forEach((event) => document.removeEventListener(event, update, true));
    };
  }, [room]);

  return blocked;
}

function StartAudioButton({ room }: Readonly<{ room: Room }>) {
  const audioBlocked = useRemoteAudioBlocked(room);
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const { t } = useWebinarT('webinars');

  // Le onClick du hook LiveKit await startAudio() sans intercepter le rejet : un second refus
  // repartirait en promesse non traitée. On garde le hook pour l'état, pas pour le handler.
  const handleClick = () => {
    void room.startAudio().catch((error: unknown) => {
      console.warn('LiveKit: reprise du son refusée malgré le clic', error);
    });
  };

  if (!audioBlocked) return null;

  return (
    <button
      onClick={handleClick}
      className="absolute hover:bg-black/80 cursor-pointer flex-col gap-6 left-1/2 flex flex-wrap items-center justify-center -translate-x-1/2 z-200 top-1/2 -translate-y-1/2 w-full  md:w-xl h-full lg:h-80 shadow-2xl backdrop-blur-sm bg-black/70 rounded-3xl p-2 md:p-8"
    >
      <div
        className="flex items-center justify-center p-3 rounded-xl"
        style={{
          backgroundColor: currentWorkspace?.visualIdentity?.secondaryBackgroundColor
            ? `${currentWorkspace?.visualIdentity?.secondaryBackgroundColor}20`
            : '#ffffff',
        }}
      >
        <Volume2
          className="size-9"
          style={{ color: currentWorkspace?.visualIdentity?.secondaryBackgroundColor ?? '#ffffff' }}
        />
      </div>
      <p className="text-white text-xl md:text-3xl font-bold wrap-break-word w-full">
        {t('live.youtube.unmute.title')}
      </p>
      <p className="text-white text-xs md:text-sm font-normal wrap-break-word w-full">
        {t('live.youtube.unmute.subtitle')}
      </p>
    </button>
  );
}

export function LiveKitRoom({
  roomName,
  token,
  isAdmin,
  isModerator,
  userId,
  workspaceId,
  webinarId,
  sessionId,
  handleTriggerEvent,
  onVideoLayoutChange,
  videoPlayerStateRef,
  hasActiveTab,
  isSpeaker,
  speakerTracksEnabled,
  participantDisplayName,
  presenterTracksEnabled,
  removedFromLive,
  cameraEnabled,
  microphoneEnabled,
  videoDeviceId,
  audioDeviceId,
  onLocalCameraEnabledChange,
  onLocalMicrophoneEnabledChange,
  screenShareControlRef,
  onRoomReady,
}: {
  roomName: string;
  token: string;
  isAdmin?: boolean;
  isModerator?: boolean;
  userId: string;
  workspaceId?: number;
  webinarId?: number;
  sessionId?: number;
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  onVideoLayoutChange?: (state: VideoLayoutState) => void;
  videoPlayerStateRef?: RefObject<VideoPlayerStateControl | null>;
  hasActiveTab?: boolean;
  isSpeaker?: boolean;
  speakerTracksEnabled?: boolean;
  participantDisplayName?: string;
  presenterTracksEnabled?: boolean;
  removedFromLive?: boolean;
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
  videoDeviceId?: string;
  audioDeviceId?: string;
  /** Même setters que le pré-test (accepte aussi (prev) => !prev). */
  onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
  onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
  /** Rempli une fois la room connectée ; utilisé par l’UI admin pour setScreenShareEnabled. */
  screenShareControlRef?: RefObject<LiveKitScreenShareControl | null>;
  /** Instance `Room` pour aperçu hors contexte (ex. onglet partage d’écran). */
  onRoomReady?: (room: Room | null) => void;
}) {
  const [isMediaInitializing, setIsMediaInitializing] = useState(true);
  const [isRoomReconnecting, setIsRoomReconnecting] = useState(false);
  const [roomFailure, setRoomFailure] = useState<RoomFailure | null>(null);
  /** Horodatage de la tentative en cours ; posé au montage, remis à chaque « Réessayer ». */
  const attemptStartedAtRef = useRef(0);
  const reportedFailureRef = useRef(false);
  /** Déconnexion demandée par la page (retry) : à distinguer de celle du SDK sur un onglet gelé. */
  const intentionalLeaveRef = useRef(false);
  const lastDisconnectRef = useRef<DisconnectDecision | null>(null);
  const roomFailureRef = useRef<RoomFailure | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const autoRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Présentateur admin autorisé à publier cam/micro (hors « backstage »). Mis à jour par la gate, pas par chaque toggle micro. */
  const [adminPresenterPublishAllowed, setAdminPresenterPublishAllowed] = useState(false);

  const [room] = useState(
    () =>
      new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: buildCameraCaptureOptions(videoDeviceId),
        publishDefaults: buildCameraPublishOptions(),
        audioCaptureDefaults: {
          ...(audioDeviceId && { deviceId: audioDeviceId }),
        },
      }),
  );

  useEffect(() => {
    onRoomReady?.(room);
    return () => {
      onRoomReady?.(null);
    };
  }, [room, onRoomReady]);

  /** Applique le plafond après publication : les encodages ne sont connus qu'une fois la piste négociée. */
  useEffect(() => {
    const onLocalTrackPublished = (publication: TrackPublication) => {
      if (publication.source !== Track.Source.Camera) return;
      const sender = publication.track instanceof LocalVideoTrack ? publication.track.sender : null;
      if (!sender) return;
      const params = sender.getParameters();
      const capped = capCameraEncodings(params.encodings);
      if (!capped) return;
      void sender.setParameters({ ...params, encodings: capped }).catch((error: unknown) => {
        console.warn('LiveKit: plafond de débit caméra non appliqué', error);
      });
    };

    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    return () => {
      room.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    };
  }, [room]);

  const resumeAudioPlayback = useMemo(
    () =>
      createAudioPlaybackResumer(room, () => isRemoteAudioBlocked(getRemoteAudioElements(room))),
    [room],
  );

  /**
   * Démarre la lecture audio après souscription ; pas la connexion signal (gérée par
   * LiveKitSessionRoot). `Reconnected` est indispensable : une reprise de session conserve les
   * pistes déjà souscrites, donc ni TrackSubscribed ni le onConnected du SDK (câblé sur le seul
   * RoomEvent.Connected) ne se déclenchent.
   */
  useEffect(() => {
    const onAudioResumeNeeded: () => void = () => {
      setTimeout(resumeAudioPlayback, AUDIO_RESUME_DELAY_MS);
    };
    room.on(RoomEvent.TrackSubscribed, onAudioResumeNeeded);
    room.on(RoomEvent.Reconnected, onAudioResumeNeeded);
    return () => {
      room.off(RoomEvent.TrackSubscribed, onAudioResumeNeeded);
      room.off(RoomEvent.Reconnected, onAudioResumeNeeded);
    };
  }, [room, resumeAudioPlayback]);

  const role = isAdmin
    ? 'presenter'
    : isModerator
      ? 'moderator'
      : isSpeaker
        ? 'speaker'
        : 'unknown';
  // Un téléphone qui se verrouille seul coupe WebRTC : l'invité garde l'écran allumé le temps du live.
  useScreenWakeLock(isSpeaker === true);

  const handleRoomErrorRef = useRef<(error: Error) => void>(() => {});

  const clearAutoRetry = useCallback(() => {
    if (autoRetryTimerRef.current) {
      clearTimeout(autoRetryTimerRef.current);
      autoRetryTimerRef.current = null;
    }
  }, []);

  const liveKitServerUrl = import.meta.env.VITE_LIVEKIT_WS_URL as string | undefined;

  /**
   * Reconnexion sans rechargement, par appel direct : basculer la prop `connect` du hook ne suffit
   * pas, React fusionne les deux rendus et le hook ne voit rien. Le message d'échec reste affiché
   * jusqu'à `Connected`, sinon l'écran clignote à chaque essai.
   */
  const retryConnection = useCallback(() => {
    clearAutoRetry();
    attemptStartedAtRef.current = Date.now();
    if (!liveKitServerUrl || !token) return;
    intentionalLeaveRef.current = true;
    void room
      .disconnect()
      .then(() => {
        // Le SDK sort de `disconnect()` sans rien émettre quand la room est déjà déconnectée,
        // ce qui est le cas de tous les retrys : sans ça le drapeau resterait armé et la
        // déconnexion suivante (onglet gelé) passerait pour volontaire.
        intentionalLeaveRef.current = false;
        return room.connect(liveKitServerUrl, token, { autoSubscribe: true });
      })
      .catch((error: unknown) => {
        if (error instanceof Error) handleRoomErrorRef.current(error);
      });
  }, [room, clearAutoRetry, liveKitServerUrl, token]);

  /**
   * Le hook LiveKit ne reconnecte pas de lui-même après une perte : la reprise vit ici, bornée aux
   * cas où elle a un sens. Un doublon d'identité ou un retrait ne doit jamais retenter.
   */
  const failConnection = useCallback(
    (failure: RoomFailure, autoRetry: boolean = shouldAutoRetry(failure)) => {
      setRoomFailure(failure);
      if (!reportedFailureRef.current) {
        reportedFailureRef.current = true;
        reportRoomFailure(failure, role);
      }
      clearAutoRetry();
      if (autoRetry) {
        autoRetryTimerRef.current = setTimeout(retryConnection, ROOM_AUTO_RETRY_DELAY_MS);
      }
    },
    [role, clearAutoRetry, retryConnection],
  );

  const handleRoomError = useCallback(
    (error: Error) => {
      const failure = classifyRoomError(error);
      if (!failure) return;
      console.error('Erreur LiveKit:', error);
      failConnection(failure);
    },
    [failConnection],
  );
  useEffect(() => {
    handleRoomErrorRef.current = handleRoomError;
  }, [handleRoomError]);

  useEffect(() => {
    attemptStartedAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    const handleConnected = () => {
      clearAutoRetry();
      setRoomFailure(null);
      reportedFailureRef.current = false;
      lastDisconnectRef.current = null;
      intentionalLeaveRef.current = false;
    };
    const handleDisconnected = (reason?: DisconnectReason) => {
      const intentional = intentionalLeaveRef.current;
      intentionalLeaveRef.current = false;
      const decision = decideAfterDisconnect(reason, intentional);
      lastDisconnectRef.current = decision;
      if (decision.failure) failConnection(decision.failure, decision.autoRetry);
    };
    // La reprise du SDK repart de zéro pour le chien de garde : il ne mesure que ce qui la suit.
    const handleReconnecting = () => {
      attemptStartedAtRef.current = Date.now();
    };
    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.SignalReconnecting, handleReconnecting);
    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.SignalReconnecting, handleReconnecting);
    };
  }, [room, clearAutoRetry, failConnection]);

  useEffect(() => {
    roomFailureRef.current = roomFailure;
  }, [roomFailure]);

  /**
   * Retour au premier plan (écran déverrouillé, autre appli, notification) : une room tombée
   * pendant le sommeil se reconnecte tout de suite, une reprise du SDK en cours se laisse finir.
   */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const state = toRoomConnectionState(room.state);
      const action = decideOnForegroundReturn(
        state,
        lastDisconnectRef.current,
        roomFailureRef.current,
      );
      const hiddenForMs = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : null;
      hiddenAtRef.current = null;
      if (action === 'none') return;
      reportForegroundReturn(role, { state, action, hiddenForMs });
      if (action === 'wait') {
        // Seule une reprise du SDK justifie de réarmer le chien de garde : une connexion initiale
        // qui patine doit finir par afficher l'échec, même si l'invité bascule d'appli.
        if (state === 'reconnecting') attemptStartedAtRef.current = Date.now();
        return;
      }
      retryConnection();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [room, role, retryConnection]);

  useEffect(() => clearAutoRetry, [clearAutoRetry]);

  /** Une connexion qui traîne sans erreur émise doit finir par le dire, sinon l'invité attend à l'aveugle. */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (roomFailure) return;
      const state = toRoomConnectionState(room.state);
      if (hasRoomConnectionTimedOut(attemptStartedAtRef.current, Date.now(), state)) {
        failConnection({ kind: 'timeout', message: 'room not connected within the allowed delay' });
      }
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [room, roomFailure, failConnection]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleConnectionStateChanged = () => {
      setIsRoomReconnecting(toRoomConnectionState(room.state) === 'reconnecting');
      if (room.state === 'connected') {
        timeoutId = setTimeout(() => {
          setIsMediaInitializing(false);
        }, 3000);
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        setIsMediaInitializing(true);
      }
    };

    handleConnectionStateChanged();
    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [room]);

  /** Gate présentateur admin + désactivation backstage ; les toggles micro/caméra sont dans des effets séparés pour ne pas relancer switchActiveDevice(videoinput) à chaque changement de micro (coupe le partage d’écran sur Chrome). */
  useEffect(() => {
    if (!isAdmin) {
      setAdminPresenterPublishAllowed(false);
      return;
    }

    const syncAdminPresenterGate = () => {
      const allowed = computeAdminPresenterPublishAllowed(
        room,
        removedFromLive,
        presenterTracksEnabled,
      );
      setAdminPresenterPublishAllowed((prev) => (prev === allowed ? prev : allowed));

      if (room.state !== 'connected') return;

      if (!allowed) {
        void unpublishAdminPresenterTracks(room, removedFromLive);
      }
    };

    syncAdminPresenterGate();

    room.on(RoomEvent.Connected, syncAdminPresenterGate);
    room.on(RoomEvent.ParticipantConnected, syncAdminPresenterGate);
    room.on(RoomEvent.ParticipantDisconnected, syncAdminPresenterGate);

    return () => {
      room.off(RoomEvent.Connected, syncAdminPresenterGate);
      room.off(RoomEvent.ParticipantConnected, syncAdminPresenterGate);
      room.off(RoomEvent.ParticipantDisconnected, syncAdminPresenterGate);
    };
  }, [room, isAdmin, presenterTracksEnabled, removedFromLive]);

  useEffect(() => {
    if (!isAdmin || !adminPresenterPublishAllowed) return;

    const applyPresenterCamera = async () => {
      if (room.state !== 'connected') return;

      const wantCam = cameraEnabled ?? true;
      try {
        if (wantCam) {
          await room.localParticipant.setCameraEnabled(true);
          if (videoDeviceId && room.switchActiveDevice) {
            await room.switchActiveDevice('videoinput', videoDeviceId);
          }
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }
        await ensureLocalCaptureMatchesIntent(room, {
          wantCam,
          wantMic: microphoneEnabled ?? true,
          cameraEnabled,
          microphoneEnabled,
          videoDeviceId,
          audioDeviceId,
        });
      } catch (error) {
        console.error('Error syncing presenter camera:', error);
      }
    };

    void applyPresenterCamera();

    const handleConnected = () => {
      void applyPresenterCamera();
    };

    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, isAdmin, adminPresenterPublishAllowed, cameraEnabled, videoDeviceId]);

  useEffect(() => {
    if (!isAdmin || !adminPresenterPublishAllowed) return;

    const applyPresenterMic = async () => {
      if (room.state !== 'connected') return;

      const wantMic = microphoneEnabled ?? true;
      try {
        if (wantMic) {
          await room.localParticipant.setMicrophoneEnabled(true);
          if (audioDeviceId && room.switchActiveDevice) {
            await room.switchActiveDevice('audioinput', audioDeviceId);
          }
        } else {
          await room.localParticipant.setMicrophoneEnabled(false);
        }
        await ensureLocalCaptureMatchesIntent(room, {
          wantCam: cameraEnabled ?? true,
          wantMic,
          cameraEnabled,
          microphoneEnabled,
          videoDeviceId,
          audioDeviceId,
        });
      } catch (error) {
        console.error('Error syncing presenter microphone:', error);
      }
    };

    void applyPresenterMic();

    const handleConnected = () => {
      void applyPresenterMic();
    };

    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, isAdmin, adminPresenterPublishAllowed, microphoneEnabled, audioDeviceId]);

  useEffect(() => {
    if (!isModerator) return;

    const unpublishModeratorTracks = async () => {
      if (room.state !== 'connected') return;
      try {
        await unpublishLocalParticipantTracks(room.localParticipant, {
          includeScreenShare: false,
        });
        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setMicrophoneEnabled(false);
      } catch (error) {
        console.error('Error unpublishing moderator tracks:', error);
      }
    };

    const handleConnected = () => {
      void unpublishModeratorTracks();
    };

    if (room.state === 'connected') {
      void unpublishModeratorTracks();
    }
    room.on('connected', handleConnected);
    return () => {
      room.off('connected', handleConnected);
    };
  }, [room, isModerator]);

  const speakerPublishAllowed = isSpeaker === true && speakerTracksEnabled === true;

  useEffect(() => {
    const unpublishSpeakerTracks = async () => {
      if (room.state !== 'connected') return;

      try {
        await unpublishLocalParticipantTracks(room.localParticipant, {
          includeScreenShare: true,
        });

        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setMicrophoneEnabled(false);
      } catch (error) {
        console.error('Error unpublishing speaker tracks:', error);
      }
    };

    const shouldUnpublish = !isSpeaker || speakerTracksEnabled !== true;

    if (!shouldUnpublish) {
      return undefined;
    }

    const run = () => {
      void unpublishSpeakerTracks();
    };

    if (room.state === 'connected') {
      run();
    }
    room.on(RoomEvent.Connected, run);

    return () => {
      room.off(RoomEvent.Connected, run);
    };
  }, [room, isSpeaker, speakerTracksEnabled]);

  useEffect(() => {
    if (!speakerPublishAllowed) return;

    const applySpeakerCamera = async () => {
      if (room.state !== 'connected') return;

      const wantCam = cameraEnabled ?? true;
      try {
        if (wantCam) {
          await room.localParticipant.setCameraEnabled(true);
          if (videoDeviceId && room.switchActiveDevice) {
            await room.switchActiveDevice('videoinput', videoDeviceId);
          }
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }
        await ensureLocalCaptureMatchesIntent(room, {
          wantCam,
          wantMic: microphoneEnabled ?? true,
          cameraEnabled,
          microphoneEnabled,
          videoDeviceId,
          audioDeviceId,
        });
      } catch (error) {
        console.error('Error syncing speaker camera:', error);
      }
    };

    void applySpeakerCamera();

    const handleConnected = () => {
      void applySpeakerCamera();
    };

    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, speakerPublishAllowed, cameraEnabled, videoDeviceId]);

  useEffect(() => {
    if (!speakerPublishAllowed) return;

    const applySpeakerMic = async () => {
      if (room.state !== 'connected') return;

      const wantMic = microphoneEnabled ?? true;
      try {
        if (wantMic) {
          await room.localParticipant.setMicrophoneEnabled(true);
          if (audioDeviceId && room.switchActiveDevice) {
            await room.switchActiveDevice('audioinput', audioDeviceId);
          }
        } else {
          await room.localParticipant.setMicrophoneEnabled(false);
        }
        await ensureLocalCaptureMatchesIntent(room, {
          wantCam: cameraEnabled ?? true,
          wantMic,
          cameraEnabled,
          microphoneEnabled,
          videoDeviceId,
          audioDeviceId,
        });
      } catch (error) {
        console.error('Error syncing speaker microphone:', error);
      }
    };

    void applySpeakerMic();

    const handleConnected = () => {
      void applySpeakerMic();
    };

    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, speakerPublishAllowed, microphoneEnabled, audioDeviceId]);

  /**
   * Sync UI (cam/mic intent) quand la modération mute une piste publiée depuis le serveur.
   * Sinon le flux est muet mais les contrôles peuvent rester visuellement « on ».
   */
  useEffect(() => {
    if (isModerator) return;
    if (!isSpeaker && !isAdmin) return;
    if (!onLocalMicrophoneEnabledChange || !onLocalCameraEnabledChange) return;

    const isLocal = (participant: Participant) =>
      participant.sid === room.localParticipant.sid &&
      participant.identity === room.localParticipant.identity;

    const apply = (muted: boolean, publication: TrackPublication, participant: Participant) => {
      if (!isLocal(participant)) return;
      switch (publication.source) {
        case Track.Source.Microphone:
          onLocalMicrophoneEnabledChange(!muted);
          break;
        case Track.Source.Camera:
          onLocalCameraEnabledChange(!muted);
          break;
        default:
          break;
      }
    };

    const onMuted = (publication: TrackPublication, participant: Participant) =>
      apply(true, publication, participant);
    const onUnmuted = (publication: TrackPublication, participant: Participant) =>
      apply(false, publication, participant);

    room.on(RoomEvent.TrackMuted, onMuted);
    room.on(RoomEvent.TrackUnmuted, onUnmuted);

    return () => {
      room.off(RoomEvent.TrackMuted, onMuted);
      room.off(RoomEvent.TrackUnmuted, onUnmuted);
    };
  }, [
    room,
    isModerator,
    isSpeaker,
    isAdmin,
    onLocalMicrophoneEnabledChange,
    onLocalCameraEnabledChange,
  ]);

  useEffect(() => {
    if (!isSpeaker || !participantDisplayName?.trim()) return;
    if (room.state !== 'connected') return;

    const label = participantDisplayName.trim();
    const apply = async () => {
      try {
        await room.localParticipant.setMetadata(JSON.stringify({ displayName: label }));
      } catch (e) {
        console.error('Speaker setMetadata(displayName)', e);
      }
    };
    void apply();
  }, [room, isSpeaker, participantDisplayName, speakerTracksEnabled]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) {
      return;
    }
    if (isModerator) {
      return;
    }

    let debounceId: ReturnType<typeof setTimeout> | null = null;

    const onDeviceChange = () => {
      if (debounceId !== null) {
        clearTimeout(debounceId);
      }
      debounceId = setTimeout(() => {
        debounceId = null;
        void recoverLocalMediaAfterDeviceChange(room, {
          isAdmin,
          isSpeaker,
          removedFromLive,
          presenterTracksEnabled,
          speakerTracksEnabled,
          cameraEnabled,
          microphoneEnabled,
          videoDeviceId,
          audioDeviceId,
        });
      }, 400);
    };

    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
      if (debounceId !== null) {
        clearTimeout(debounceId);
      }
    };
  }, [
    room,
    isModerator,
    isAdmin,
    isSpeaker,
    removedFromLive,
    presenterTracksEnabled,
    speakerTracksEnabled,
    cameraEnabled,
    microphoneEnabled,
    videoDeviceId,
    audioDeviceId,
  ]);

  useEffect(() => {
    if (token && roomName && !import.meta.env.VITE_LIVEKIT_WS_URL) {
      console.error('VITE_LIVEKIT_WS_URL non définie');
    }
  }, [token, roomName]);

  useEffect(() => {
    const ref = screenShareControlRef;
    if (!ref) {
      return;
    }
    const api: LiveKitScreenShareControl = {
      startScreenShare: async () => {
        if (room.state !== 'connected') {
          throw new Error('Room not connected');
        }
        const { capture, publish } = buildScreenShareOptions();
        await room.localParticipant.setScreenShareEnabled(true, capture, publish);
      },
      stopScreenShare: async () => {
        if (room.state !== 'connected') {
          return;
        }
        await room.localParticipant.setScreenShareEnabled(false);
      },
      isScreenSharing: () => {
        const pub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        return pub?.track != null;
      },
    };
    ref.current = api;
    return () => {
      ref.current = null;
    };
  }, [room, screenShareControlRef]);

  if (!token) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full">
        <div>
          <Loader2 size={24} className="animate-spin" />
        </div>
      </div>
    );
  }

  const shouldConnectToLiveKit = Boolean(roomName && liveKitServerUrl);

  return (
    <LiveKitSessionRoot
      room={room}
      serverUrl={liveKitServerUrl}
      token={token}
      connect={shouldConnectToLiveKit}
      connectOptions={{ autoSubscribe: true }}
      onConnected={resumeAudioPlayback}
      onError={handleRoomError}
      className="relative h-full w-full"
    >
      <StartAudioButton room={room} />
      <AudioRenderer room={room} />
      <LiveKitRoomContent
        isAdmin={isAdmin}
        isModerator={isModerator}
        userId={userId}
        workspaceId={workspaceId}
        webinarId={webinarId}
        sessionId={sessionId}
        handleTriggerEvent={handleTriggerEvent}
        onVideoLayoutChange={onVideoLayoutChange}
        videoPlayerStateRef={videoPlayerStateRef}
        hasActiveTab={hasActiveTab}
        isSpeaker={isSpeaker}
        speakerTracksEnabled={speakerTracksEnabled}
        cameraEnabled={cameraEnabled}
        microphoneEnabled={microphoneEnabled}
        isMediaInitializing={isMediaInitializing}
        isRoomReconnecting={isRoomReconnecting}
        roomFailure={roomFailure}
        onRetryConnection={retryConnection}
        onLocalCameraEnabledChange={onLocalCameraEnabledChange}
        onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
      />
    </LiveKitSessionRoot>
  );
}
